import { Check, X, AlertTriangle, Stethoscope } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AppointmentEvent, AppointmentStatus } from "../../../shared/types/appointment";
import { appointmentStatusLabel } from "../../../shared/format";

/** Một bước trên thanh tiến trình: trạng thái thật, hoặc bước ảo "Đang khám" (`IN_EXAM`). */
export type ProgressStepKey = AppointmentStatus | "IN_EXAM";

export interface ProgressStep {
  key: ProgressStepKey;
  label: string;
}

/** Đường đi bình thường của một lịch hẹn, bước "Đang thực hiện" suy từ `inExam` (lần khám đang mở). */
const HAPPY_PATH: ProgressStepKey[] = ["REQUESTED", "CONFIRMED", "CHECKED_IN", "IN_EXAM", "COMPLETED"];

const LABEL: Record<ProgressStepKey, string> = {
  ...appointmentStatusLabel,
  IN_EXAM: "Đang thực hiện",
};

/**
 * Dãy bước và vị trí hiện tại, theo cách SalePro vẽ `Steps` trên màn Chi tiết đặt lịch:
 * đường thuận là 5 bước; lịch bị huỷ / không đến thì vẽ các bước ĐÃ ĐI QUA rồi kết bằng bước
 * kết thúc đó. Khác SalePro một chỗ: SalePro luôn vẽ `Mới → Đã hủy`, còn ở đây các bước đã qua
 * được đọc từ lịch sử (`events[].toStatus`) — một lịch huỷ sau khi đã nhận khám vẽ đủ ba bước
 * trước dấu huỷ. Không có lịch sử thì lùi về đúng hình của SalePro.
 */
export function progressSteps(
  status: AppointmentStatus,
  inExam: boolean,
  events: Pick<AppointmentEvent, "toStatus">[] = [],
): { steps: ProgressStep[]; current: number } {
  if (status === "CANCELLED" || status === "NO_SHOW") {
    const reached = new Set<ProgressStepKey>(["REQUESTED"]);
    for (const ev of events) {
      if (ev.toStatus && ev.toStatus !== status && HAPPY_PATH.includes(ev.toStatus)) {
        reached.add(ev.toStatus);
      }
    }
    // Hình mặc định của SalePro khi không biết gì hơn: không đến ⇒ đã từng được xác nhận.
    if (status === "NO_SHOW" && events.length === 0) reached.add("CONFIRMED");
    const before = HAPPY_PATH.filter((k) => k !== "COMPLETED" && k !== "IN_EXAM" && reached.has(k));
    const steps = [...before, status].map((key) => ({ key, label: LABEL[key] }));
    return { steps, current: steps.length - 1 };
  }
  const steps = HAPPY_PATH.map((key) => ({ key, label: LABEL[key] }));
  const currentKey: ProgressStepKey = status === "CHECKED_IN" && inExam ? "IN_EXAM" : status;
  return { steps, current: HAPPY_PATH.indexOf(currentKey) };
}

interface Props {
  status: AppointmentStatus;
  inExam: boolean;
  events?: Pick<AppointmentEvent, "toStatus">[];
  className?: string;
}

/**
 * Thanh tiến trình trạng thái lịch hẹn (bản Tailwind/shadcn của antd `Steps size="small"` trên
 * màn SalePro): bước đã qua tô màu chính kèm dấu tick, bước hiện tại viền đậm, bước chưa tới mờ.
 * Bước kết thúc xấu (huỷ / không đến) đổi sang màu cảnh báo để không đọc nhầm thành "hoàn tất".
 */
export function AppointmentProgress({ status, inExam, events = [], className }: Props) {
  const { steps, current } = progressSteps(status, inExam, events);
  const negative = status === "CANCELLED" || status === "NO_SHOW";

  return (
    <ol
      className={cn("flex items-start gap-1 overflow-x-auto pb-1", className)}
      aria-label="Tiến trình lịch hẹn"
      data-testid="appointment-progress"
    >
      {steps.map((step, i) => {
        // Đã khám xong là bước cuối của đường thuận: vẽ nó như đã qua (tick), không phải "đang ở".
        const active = i === current;
        const done = i < current || (active && status === "COMPLETED");
        const last = i === steps.length - 1;
        const terminalBad = active && negative;
        return (
          <li
            key={step.key}
            className="flex min-w-0 flex-1 items-start"
            aria-current={active ? "step" : undefined}
            data-step={step.key}
            data-state={active ? "active" : done ? "done" : "todo"}
          >
            <div className="flex min-w-0 flex-col items-center gap-1.5 text-center">
              <span
                className={cn(
                  "flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors",
                  done && "border-primary bg-primary text-primary-foreground",
                  active && !done && !terminalBad && "border-primary bg-background text-primary",
                  terminalBad && status === "CANCELLED" && "border-destructive bg-destructive text-destructive-foreground",
                  terminalBad && status === "NO_SHOW" && "border-amber-500 bg-amber-500 text-white",
                  !done && !active && "border-border bg-background text-muted-foreground",
                )}
                aria-hidden="true"
              >
                {done ? (
                  <Check className="size-3.5" />
                ) : terminalBad && status === "CANCELLED" ? (
                  <X className="size-3.5" />
                ) : terminalBad && status === "NO_SHOW" ? (
                  <AlertTriangle className="size-3.5" />
                ) : step.key === "IN_EXAM" && active ? (
                  <Stethoscope className="size-3.5" />
                ) : (
                  i + 1
                )}
              </span>
              <span
                className={cn(
                  "max-w-[6.5rem] text-[11px] leading-tight sm:text-xs",
                  active ? "font-semibold text-foreground" : done ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </div>
            {!last && (
              <span
                className={cn("mt-3.5 h-0.5 min-w-3 flex-1", i < current ? "bg-primary" : "bg-border")}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
